import { SPEND_CHART_COLORS } from "@/components/charts";
import { GLOBAL_DATE_FORMAT } from "@/constants/date-formats";
import { formatDateWithToken } from "@/utils/date";
import { normalizeDashboardDateRange, parseLocalDate, toLocalDateKey } from "./dashboard-date-range";
import type {
    ApprovalsView,
    ChargeApprovalItem,
    ChargeItemsStatusDay,
    DashboardDateRange,
    DashboardFilters,
    SpendDimension,
    SpendSegment,
    UnspentChargeItem,
    UnspentView,
} from "./dashboard-types";

type DashboardRangeKey = 7 | 30 | 90 | 365;

const RANGE_SCALE: Record<DashboardRangeKey, number> = {
    7: 7 / 30,
    30: 1,
    90: 3,
    365: 12,
};

const SPEND_BY_CATEGORY: Record<DashboardRangeKey, SpendSegment[]> = {
    7: [
        { id: "airfare", label: "Airfare", amount: 12450, color: SPEND_CHART_COLORS[0] },
        { id: "hotel", label: "Hotel stay", amount: 10280, color: SPEND_CHART_COLORS[1] },
        { id: "car", label: "Car rental", amount: 5120, color: SPEND_CHART_COLORS[2] },
        { id: "rideshare", label: "Ride share", amount: 1140, color: SPEND_CHART_COLORS[3] },
        { id: "meals", label: "Client meals", amount: 540, color: SPEND_CHART_COLORS[4] },
        { id: "other", label: "Other", amount: 810, color: SPEND_CHART_COLORS[5] },
    ],
    30: [
        { id: "airfare", label: "Airfare", amount: 55136, color: SPEND_CHART_COLORS[0] },
        { id: "hotel", label: "Hotel stay", amount: 49282, color: SPEND_CHART_COLORS[1] },
        { id: "car", label: "Car rental", amount: 24512, color: SPEND_CHART_COLORS[2] },
        { id: "rideshare", label: "Ride share", amount: 5456, color: SPEND_CHART_COLORS[3] },
        { id: "meals", label: "Client meals", amount: 2564, color: SPEND_CHART_COLORS[4] },
        { id: "other", label: "Other", amount: 3845, color: SPEND_CHART_COLORS[5] },
    ],
    90: [
        { id: "airfare", label: "Airfare", amount: 148920, color: SPEND_CHART_COLORS[0] },
        { id: "hotel", label: "Hotel stay", amount: 132640, color: SPEND_CHART_COLORS[1] },
        { id: "car", label: "Car rental", amount: 66840, color: SPEND_CHART_COLORS[2] },
        { id: "rideshare", label: "Ride share", amount: 14820, color: SPEND_CHART_COLORS[3] },
        { id: "meals", label: "Client meals", amount: 6920, color: SPEND_CHART_COLORS[4] },
        { id: "other", label: "Other", amount: 10360, color: SPEND_CHART_COLORS[5] },
    ],
    365: [
        { id: "airfare", label: "Airfare", amount: 612400, color: SPEND_CHART_COLORS[0] },
        { id: "hotel", label: "Hotel stay", amount: 548900, color: SPEND_CHART_COLORS[1] },
        { id: "car", label: "Car rental", amount: 276500, color: SPEND_CHART_COLORS[2] },
        { id: "rideshare", label: "Ride share", amount: 61200, color: SPEND_CHART_COLORS[3] },
        { id: "meals", label: "Client meals", amount: 28600, color: SPEND_CHART_COLORS[4] },
        { id: "other", label: "Other", amount: 42800, color: SPEND_CHART_COLORS[5] },
    ],
};

const SPEND_BY_USER_BASE: SpendSegment[] = [
    { id: "jacob", label: "Jacob Anderson", amount: 18240, color: SPEND_CHART_COLORS[0] },
    { id: "kimberly", label: "Kimberly Olsen", amount: 16480, color: SPEND_CHART_COLORS[1] },
    { id: "jose", label: "Jose Rodriguez", amount: 14220, color: SPEND_CHART_COLORS[2] },
    { id: "marc", label: "Marc Poplar", amount: 11860, color: SPEND_CHART_COLORS[3] },
    { id: "william", label: "William Evans", amount: 9640, color: SPEND_CHART_COLORS[4] },
    { id: "other-users", label: "Other", amount: 35355, color: SPEND_CHART_COLORS[5] },
];

const SPEND_BY_DEPARTMENT_BASE: SpendSegment[] = [
    { id: "sales", label: "Sales", amount: 42120, color: SPEND_CHART_COLORS[0] },
    { id: "engineering", label: "Engineering", amount: 31840, color: SPEND_CHART_COLORS[1] },
    { id: "operations", label: "Operations", amount: 24680, color: SPEND_CHART_COLORS[2] },
    { id: "marketing", label: "Marketing", amount: 18920, color: SPEND_CHART_COLORS[3] },
    { id: "finance", label: "Finance", amount: 11240, color: SPEND_CHART_COLORS[4] },
    { id: "other-dept", label: "Other", amount: 8885, color: SPEND_CHART_COLORS[5] },
];

const UNSPENT_BY_USER_BASE: Array<Omit<UnspentChargeItem, "oldestChargeDate"> & { oldestDaysAgo: number }> = [
    { id: "jacob", name: "Jacob Anderson", count: 8, oldestDaysAgo: 28 },
    { id: "kimberly", name: "Kimberly Olsen", count: 5, oldestDaysAgo: 24 },
    { id: "jose", name: "Jose Rodriguez", count: 5, oldestDaysAgo: 21 },
    { id: "marc", name: "Marc Poplar", count: 3, oldestDaysAgo: 18 },
    { id: "william", name: "William Evans", count: 2, oldestDaysAgo: 14 },
];

const UNSPENT_BY_DEPARTMENT_BASE: Array<Omit<UnspentChargeItem, "oldestChargeDate"> & { oldestDaysAgo: number }> = [
    { id: "sales", name: "Sales", count: 11, oldestDaysAgo: 26 },
    { id: "engineering", name: "Engineering", count: 7, oldestDaysAgo: 22 },
    { id: "operations", name: "Operations", count: 5, oldestDaysAgo: 19 },
    { id: "marketing", name: "Marketing", count: 3, oldestDaysAgo: 15 },
];

const UNSPENT_BY_CATEGORY_BASE: Array<Omit<UnspentChargeItem, "oldestChargeDate"> & { oldestDaysAgo: number }> = [
    { id: "airfare", name: "Airfare", count: 14, oldestDaysAgo: 27 },
    { id: "hotel", name: "Hotel stay", count: 11, oldestDaysAgo: 23 },
    { id: "car", name: "Car rental", count: 7, oldestDaysAgo: 20 },
    { id: "meals", name: "Client meals", count: 4, oldestDaysAgo: 16 },
];

const APPROVALS: ChargeApprovalItem[] = [
    { id: "william", name: "William Evans", initials: "WE", statusLabel: "Submitted items: 24", view: "submitted" },
    { id: "samantha", name: "Samantha Locker", initials: "SL", statusLabel: "Clarification request: 16", view: "clarification" },
    { id: "neil", name: "Neil Bowie", initials: "NB", statusLabel: "Submitted items: 15", view: "submitted" },
    { id: "jacob", name: "Jacob Anderson", initials: "JA", statusLabel: "Submitted items: 12", view: "submitted" },
    { id: "kimberly", name: "Kimberly Olsen", initials: "KO", statusLabel: "Clarification request: 9", view: "clarification" },
];

const CHARGE_ITEMS_STATUS_BASE = {
    notAddedToReport: 180,
    addedToReport: 220,
    review: 140,
    accountingReview: 90,
    approved: 260,
} as const;

function scaleSpendSegments(segments: SpendSegment[], factor: number): SpendSegment[] {
    return segments.map((segment) => ({
        ...segment,
        amount: Math.max(1, Math.round(segment.amount * factor)),
    }));
}

function buildSpendByDimension(base: SpendSegment[]): Record<DashboardRangeKey, SpendSegment[]> {
    return {
        7: scaleSpendSegments(base, RANGE_SCALE[7]),
        30: base,
        90: scaleSpendSegments(base, RANGE_SCALE[90]),
        365: scaleSpendSegments(base, RANGE_SCALE[365]),
    };
}

const SPEND_BY_USER = buildSpendByDimension(SPEND_BY_USER_BASE);
const SPEND_BY_DEPARTMENT = buildSpendByDimension(SPEND_BY_DEPARTMENT_BASE);

function offsetDateKey(endDate: string, daysBack: number): string {
    const date = parseLocalDate(endDate);
    date.setDate(date.getDate() - daysBack);
    return toLocalDateKey(date);
}

function buildUnspentItems(
    base: Array<Omit<UnspentChargeItem, "oldestChargeDate"> & { oldestDaysAgo: number }>,
    rangeKey: DashboardRangeKey,
    endDate: string,
): UnspentChargeItem[] {
    const countFactor = RANGE_SCALE[rangeKey];
    const visibleItems = rangeKey === 7 ? 3 : rangeKey === 30 ? 4 : base.length;

    return base.slice(0, visibleItems).map((item) => ({
        id: item.id,
        name: item.name,
        count: Math.max(1, Math.round(item.count * countFactor)),
        oldestChargeDate: offsetDateKey(endDate, Math.min(item.oldestDaysAgo * (rangeKey / 30), rangeKey - 1)),
    }));
}

function formatChargeItemLabel(date: Date) {
    return formatDateWithToken(date, GLOBAL_DATE_FORMAT, "");
}

function createChargeItemPoint(dateKey: string, index: number): ChargeItemsStatusDay {
    const wave = index % 6;
    const date = parseLocalDate(dateKey);

    return {
        date: dateKey,
        label: formatChargeItemLabel(date),
        notAddedToReport: CHARGE_ITEMS_STATUS_BASE.notAddedToReport + wave * 10 + (index % 3) * 15,
        addedToReport: CHARGE_ITEMS_STATUS_BASE.addedToReport + wave * 12 + (index % 4) * 10,
        review: CHARGE_ITEMS_STATUS_BASE.review + wave * 8 + (index % 2) * 12,
        accountingReview: CHARGE_ITEMS_STATUS_BASE.accountingReview + wave * 7 + (index % 5) * 8,
        approved: CHARGE_ITEMS_STATUS_BASE.approved + wave * 14 + (index % 3) * 20,
    };
}

function getChargeItemsIntervalDays(days: number) {
    if (days <= 7) return 1;
    if (days <= 30) return 4;
    if (days <= 90) return 7;
    return 30;
}

function generateChargeItemsForRange(dateRange: DashboardDateRange): ChargeItemsStatusDay[] {
    const normalizedRange = normalizeDashboardDateRange(dateRange);
    const start = parseLocalDate(normalizedRange.startDate);
    const end = parseLocalDate(normalizedRange.endDate);
    const intervalDays = getChargeItemsIntervalDays(normalizedRange.days);

    const points: ChargeItemsStatusDay[] = [];
    const cursor = new Date(start);
    let index = 0;

    while (cursor <= end) {
        points.push(createChargeItemPoint(toLocalDateKey(cursor), index));
        cursor.setDate(cursor.getDate() + intervalDays);
        index += 1;
    }

    if (points.length === 0) {
        points.push(createChargeItemPoint(normalizedRange.endDate, 0));
    }

    return points;
}

function resolveRangeKey(days: number): DashboardRangeKey {
    if (days <= 7) return 7;
    if (days <= 30) return 30;
    if (days <= 90) return 90;
    return 365;
}

function getSpendData(dimension: SpendDimension, dateRange: DashboardDateRange): SpendSegment[] {
    const rangeKey = resolveRangeKey(normalizeDashboardDateRange(dateRange).days);
    const source = dimension === "user" ? SPEND_BY_USER : dimension === "department" ? SPEND_BY_DEPARTMENT : SPEND_BY_CATEGORY;

    return source[rangeKey];
}

function getUnspentData(view: UnspentView, dateRange: DashboardDateRange): UnspentChargeItem[] {
    const normalizedRange = normalizeDashboardDateRange(dateRange);
    const rangeKey = resolveRangeKey(normalizedRange.days);
    const base = view === "department" ? UNSPENT_BY_DEPARTMENT_BASE : view === "category" ? UNSPENT_BY_CATEGORY_BASE : UNSPENT_BY_USER_BASE;

    return buildUnspentItems(base, rangeKey, normalizedRange.endDate);
}

function getApprovalsData(view: ApprovalsView, dateRange: DashboardDateRange): ChargeApprovalItem[] {
    const multiplier = RANGE_SCALE[resolveRangeKey(normalizeDashboardDateRange(dateRange).days)];
    const filtered = view === "all" ? APPROVALS : APPROVALS.filter((item) => item.view === view);

    return filtered.map((item) => {
        const match = item.statusLabel.match(/(\d+)/);
        if (!match) return item;

        const count = Math.max(1, Math.round(Number(match[1]) * multiplier));
        const prefix = item.statusLabel.split(":")[0] ?? item.statusLabel;
        return {
            ...item,
            statusLabel: `${prefix}: ${count}`,
        };
    });
}

function getChargeItemsData(dateRange: DashboardDateRange): ChargeItemsStatusDay[] {
    return generateChargeItemsForRange(dateRange);
}

/** Temporary mock resolver until dashboard GraphQL endpoints are available. */
export function getDashboardMockData(filters: DashboardFilters) {
    return {
        spend: getSpendData(filters.spendDimension, filters.globalDateRange),
        unspentCharges: getUnspentData(filters.unspentView, filters.globalDateRange),
        approvals: getApprovalsData(filters.approvalsView, filters.globalDateRange),
        chargeItemsByDay: getChargeItemsData(filters.chargeItemsDateRange),
    };
}
